import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  Alert,
  Autocomplete,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  TrendingDown,
  Save,
  Close,
  Warning,
  ErrorOutline
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const MotionDiv = motion.div;

const WastageForm = ({
  open,
  onClose,
  onSave,
  initialData = null,
  products = [],
  loading = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Form state
  const [formData, setFormData] = useState({
    date: new Date(),
    product_id: '',
    quantity_wasted: '',
    reason: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Wastage reasons - consistent with parent component
  const wastageReasons = [
    { value: 'expired', label: t('inventory.wastageReasons.expired') || 'Expired', color: 'error', icon: <ErrorOutline /> },
    { value: 'damaged', label: t('inventory.wastageReasons.damaged') || 'Damaged', color: 'warning', icon: <Warning /> },
    { value: 'spoiled', label: t('inventory.wastageReasons.spoiled') || 'Spoiled', color: 'error', icon: <ErrorOutline /> },
    { value: 'returned', label: t('inventory.wastageReasons.returned') || 'Customer Return', color: 'info', icon: <Warning /> },
    { value: 'overproduction', label: t('inventory.wastageReasons.overproduction') || 'Overproduction', color: 'warning', icon: <Warning /> },
    { value: 'contaminated', label: t('inventory.wastageReasons.contaminated') || 'Contaminated', color: 'error', icon: <ErrorOutline /> },
    { value: 'other', label: t('inventory.wastageReasons.other') || 'Other', color: 'default', icon: <Warning /> }
  ];

  // Initialize form with existing data or defaults
  useEffect(() => {
    if (initialData) {
      setFormData({
        date: new Date(initialData.date),
        product_id: initialData.product_id || '',
        quantity_wasted: initialData.quantity_wasted?.toString() || '',
        reason: initialData.reason || '',
        notes: initialData.notes || ''
      });

      // Find and set selected product
      const product = products.find(p => p.item_id === initialData.product_id);
      setSelectedProduct(product || null);
    } else {
      // Reset form for new record
      setFormData({
        date: new Date(),
        product_id: '',
        quantity_wasted: '',
        reason: '',
        notes: ''
      });
      setSelectedProduct(null);
    }
    setErrors({});
  }, [initialData, products, open]);

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear specific field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Handle product selection
  const handleProductChange = (event, newValue) => {
    setSelectedProduct(newValue);
    handleInputChange('product_id', newValue?.item_id || '');
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.product_id) {
      newErrors.product_id = t('inventory.validation.productRequired') || 'Product is required';
    }

    if (!formData.quantity_wasted || parseFloat(formData.quantity_wasted) <= 0) {
      newErrors.quantity_wasted = t('inventory.validation.quantityRequired') || 'Valid quantity is required';
    }

    if (!formData.reason) {
      newErrors.reason = t('inventory.validation.reasonRequired') || 'Reason is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const submitData = {
      date: format(formData.date, 'yyyy-MM-dd'),
      product_id: formData.product_id,
      quantity_wasted: parseFloat(formData.quantity_wasted),
      reason: formData.reason,
      notes: formData.notes.trim() || null
    };

    onSave(submitData);
  };

  // Handle dialog close
  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  // Get selected reason info
  const selectedReasonInfo = wastageReasons.find(r => r.value === formData.reason);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown={loading}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)'
              : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
          color: 'white',
          borderRadius: isMobile ? 0 : '12px 12px 0 0'
        }}>
          <TrendingDown />
          {initialData 
            ? (t('inventory.wastageLog.editRecord') || 'Edit Wastage Record')
            : (t('inventory.wastageLog.addRecord') || 'Add Wastage Record')
          }
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <MotionDiv
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={3}>
              {/* Date Selection */}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('inventory.date') || 'Date'}
                  value={formData.date}
                  onChange={(newValue) => handleInputChange('date', newValue)}
                  disabled={loading}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.date,
                      helperText: errors.date,
                      sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } }
                    }
                  }}
                />
              </Grid>

              {/* Product Selection */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  value={selectedProduct}
                  onChange={handleProductChange}
                  options={products}
                  getOptionLabel={(option) => option.name || ''}
                  disabled={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('inventory.product') || 'Product'}
                      error={!!errors.product_id}
                      helperText={errors.product_id}
                      required
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key: _, ...otherProps } = props;
                    return (
                      <Box 
                        component="li" 
                        key={`product-${option.item_id}`}
                        {...otherProps} 
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}
                      >
                        {option.image ? (
                          <Avatar src={option.image} sx={{ width: 32, height: 32 }} />
                        ) : (
                          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.lighter' }}>
                            {option.name?.charAt(0) || '?'}
                          </Avatar>
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {option.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {option.category_name} • Current: {option.current_quantity || 0}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  }}
                />
              </Grid>

              {/* Selected Product Info */}
              {selectedProduct && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: 'info.lighter', 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'info.light'
                  }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: 'info.main' }}>
                      {t('inventory.selectedProduct') || 'Selected Product'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {selectedProduct.image ? (
                        <Avatar src={selectedProduct.image} sx={{ width: 40, height: 40 }} />
                      ) : (
                        <Avatar sx={{ width: 40, height: 40, bgcolor: 'primary.main' }}>
                          {selectedProduct.name?.charAt(0) || '?'}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {selectedProduct.name}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                          <Chip 
                            label={selectedProduct.category_name || 'Uncategorized'} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Current Stock: {selectedProduct.current_quantity || 0} {selectedProduct.unit_of_measure || 'pcs'}
                          </Typography>
                        </Stack>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Quantity Wasted */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('inventory.quantityWasted') || 'Quantity Wasted'}
                  type="number"
                  value={formData.quantity_wasted}
                  onChange={(e) => handleInputChange('quantity_wasted', e.target.value)}
                  error={!!errors.quantity_wasted}
                  helperText={errors.quantity_wasted}
                  disabled={loading}
                  required
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {/* Reason Selection */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.reason} required>
                  <InputLabel>{t('inventory.reason') || 'Reason'}</InputLabel>
                  <Select
                    value={formData.reason}
                    onChange={(e) => handleInputChange('reason', e.target.value)}
                    label={t('inventory.reason') || 'Reason'}
                    disabled={loading}
                    sx={{ borderRadius: 2 }}
                  >
                    {wastageReasons.map((reason) => (
                      <MenuItem key={`reason-${reason.value}`} value={reason.value}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {reason.icon}
                          <Typography>{reason.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.reason && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.reason}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              {/* Selected Reason Preview */}
              {selectedReasonInfo && (
                <Grid item xs={12}>
                  <Box sx={{ 
                    p: 2, 
                    bgcolor: `${selectedReasonInfo.color}.lighter`, 
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: `${selectedReasonInfo.color}.light`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {selectedReasonInfo.icon}
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: `${selectedReasonInfo.color}.main` }}>
                        {t('inventory.reason') || 'Reason'}: {selectedReasonInfo.label}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('inventory.notes') || 'Notes (Optional)'}
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  disabled={loading}
                  placeholder={t('inventory.wastageNotesPlaceholder') || 'Additional details about the wastage...'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {/* Validation Summary */}
              {Object.keys(errors).length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      {t('inventory.validation.pleaseFixErrors') || 'Please fix the errors above before submitting.'}
                    </Typography>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </MotionDiv>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ borderRadius: 2, minWidth: 100 }}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || Object.keys(errors).length > 0}
            startIcon={loading ? <></> : <Save />}
            sx={{
              borderRadius: 2,
              minWidth: 120,
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #ff5252 0%, #e53935 100%)',
              }
            }}
          >
            {loading 
              ? (t('common.saving') || 'Saving...') 
              : (initialData ? (t('common.update') || 'Update') : (t('common.save') || 'Save'))
            }
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default WastageForm;